import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User } from 'lucide-react';
import { formatIqd, fromMillidinars, type OpenTabSummary } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { api } from '@/lib/api';

import {
  cardAmountMd,
  filterTabs,
  isLiveTab,
  openedLabelParts,
  sameNameCounts,
  type OpenTabSource,
} from './liveTabs';

/**
 * Pick a party from everyone who currently has a tab open.
 *
 * THE reason this is one component used everywhere rather than a `<select>` per
 * screen: a busy night is thirty open tabs, and a dropdown of thirty names is
 * something you scroll and squint at while a customer waits. A searchable grid of
 * name cards is one glance and one tap — which is what a till needs.
 *
 * Deliberately shows the party's device count and running total on each card:
 * two customers share a first name often enough that the name alone is not an
 * identification, and "the one on three machines" settles it instantly. Every
 * card also says when the tab was opened, and a name that appears twice says
 * it louder — 'Yaran staff' and 'yaran staff' were one party's old paid-up tab
 * and its real one (owner, 2026-09-23), and the date is what tells them apart.
 */
export function OpenTabPicker({
  value,
  onChange,
  excludeTabId,
  autoFocus = false,
  presentOnly = false,
  source = 'open',
  variant = 'inline',
}: {
  value: string | null;
  onChange: (tabId: string, tab: OpenTabSummary) => void;
  /** Hide one party — you cannot move a tab onto itself. */
  excludeTabId?: string | null;
  autoFocus?: boolean;
  /**
   * Only parties at the venue right now (`?present=1`). The cafe wants the
   * people it can hand a drink to; the merge screens keep the full list, where
   * an absent debtor's tab is a legitimate target.
   */
  presentOnly?: boolean;
  /**
   * `live` reads `GET /tabs/live`: only parties that owe money, are playing,
   * hold a pass or were opened moments ago — the same rule the Outstanding
   * screen and checkout's close use — and each card carries what the party
   * owes. `open` is every open tab with a sitting or an open bill, for the
   * merge screens.
   */
  source?: OpenTabSource;
  /** `dialog` lets the list breathe inside a full-height modal. */
  variant?: 'inline' | 'dialog';
}) {
  const { t } = useTranslation();

  const [tabs, setTabs] = useState<OpenTabSummary[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const path =
      source === 'live' ? '/tabs/live' : presentOnly ? '/tabs/open?present=1' : '/tabs/open';
    void (async () => {
      try {
        const rows = await api.get<OpenTabSummary[]>(path);
        if (!cancelled) setTabs(rows);
      } catch {
        if (!cancelled) setError(t('tabs.picker.loadFailed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, presentOnly, source]);

  return (
    <div>
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-2.5 h-4 w-4 text-content-muted"
          style={{ insetInlineStart: '0.625rem' }}
          aria-hidden
        />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          autoFocus={autoFocus}
          placeholder={t('tabs.picker.searchPlaceholder')}
          aria-label={t('tabs.picker.searchPlaceholder')}
          className="w-full rounded-control border border-subtle bg-input px-3 py-2 ps-8 text-sm text-content outline-none focus:border-primary"
        />
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      ) : tabs === null ? (
        <p className="mt-2 text-sm text-content-muted">{t('common.loading')}</p>
      ) : (
        <OpenTabCards
          tabs={tabs}
          search={search}
          excludeTabId={excludeTabId}
          value={value}
          onChange={onChange}
          variant={variant}
        />
      )}
    </div>
  );
}

/**
 * The cards themselves, given a loaded list — separate so the markup can be
 * rendered and tested without a network.
 */
export function OpenTabCards({
  tabs,
  search,
  excludeTabId,
  value,
  onChange,
  variant = 'inline',
  now = new Date(),
}: {
  tabs: readonly OpenTabSummary[];
  search: string;
  excludeTabId?: string | null;
  value: string | null;
  onChange: (tabId: string, tab: OpenTabSummary) => void;
  variant?: 'inline' | 'dialog';
  now?: Date;
}) {
  const { t, i18n } = useTranslation();
  const { venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const visible = useMemo(
    () => filterTabs(tabs, search, excludeTabId),
    [tabs, search, excludeTabId],
  );
  // Twins among what is ON SCREEN: after typing "yaran", the two cards left
  // are the ones the cashier has to choose between.
  const twins = useMemo(() => sameNameCounts(visible), [visible]);

  if (visible.length === 0) {
    return (
      <p className="mt-2 text-sm text-content-muted">
        {tabs.length === 0 ? t('tabs.picker.noneOpen') : t('common.noResults')}
      </p>
    );
  }

  return (
    <ul
      data-open-tab-picker
      className={`mt-2 grid gap-2 overflow-auto pt-0.5 ${
        variant === 'dialog' ? 'max-h-[52vh]' : 'max-h-72'
      }`}
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))' }}
    >
      {visible.map((tab) => {
        const picked = tab.id === value;
        const amountMd = cardAmountMd(tab);
        const opened = openedLabelParts(tab.openedAt, now, i18n.language);
        const hasTwin = (twins.get(tab.id) ?? 0) > 0;
        const muted = picked ? 'text-primary-fg/80' : 'text-content-muted';
        return (
          <li key={tab.id}>
            <button
              type="button"
              data-tab-option={tab.id}
              onClick={() => onChange(tab.id, tab)}
              aria-pressed={picked}
              className={[
                'flex w-full items-center gap-2 rounded-card px-2.5 py-2 text-start transition-colors',
                picked
                  ? 'bg-primary text-primary-fg'
                  : 'border border-subtle bg-input text-content hover:bg-hover',
              ].join(' ')}
            >
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${
                  picked ? 'bg-black/20' : 'bg-overlay text-content-secondary'
                }`}
              >
                <User className="h-4 w-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{tab.name}</span>
                <span className={`tabular block truncate text-[11px] ${muted}`}>
                  {/* WHERE the party is beats how many machines: "PC 07 ·
                      Table 3" identifies a person; "2 devices" does not.
                      The count remains the fallback for a seated-nowhere
                      party and for a server that predates `locations`. */}
                  {(tab.locations?.length ?? 0) > 0
                    ? tab.locations.join(' · ')
                    : t('tabs.picker.deviceCount', { count: tab.deviceCount })}
                </span>
                <span
                  data-tab-meta
                  className={`tabular block truncate text-[11px] ${
                    hasTwin && !picked ? 'font-semibold text-content-secondary' : muted
                  }`}
                >
                  {[
                    opened.today
                      ? t('openTabs.card.openedToday', { time: opened.text })
                      : t('openTabs.card.openedOn', { date: opened.text }),
                    // The live list says what is OWED, in words, because that
                    // figure is the other half of telling twins apart; the
                    // open list keeps its bare running total.
                    amountMd !== '0'
                      ? isLiveTab(tab)
                        ? t('openTabs.card.owes', {
                            amount: formatIqd(fromMillidinars(amountMd), {
                              currencySymbol,
                              locale: i18n.language,
                            }),
                          })
                        : formatIqd(fromMillidinars(amountMd), {
                            currencySymbol,
                            locale: i18n.language,
                          })
                      : isLiveTab(tab) && !tab.running
                        ? t('openTabs.card.owesNothing')
                        : null,
                    isLiveTab(tab) && tab.running ? t('openTabs.card.playing') : null,
                  ]
                    .filter((part): part is string => part !== null)
                    .join(' · ')}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
